import {Suspense, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {number, object, string} from "yup";

import {Loader} from "@pg-design/loader-module";
import {getPhoneNumberAsObject, IPhoneFieldValue} from "@pg-design/phone-input-module";
import {CheckBoxGroupControlled, TextareaField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {IAppError} from "@pg-mono/request-state";

import {InquiryButtonTypeName} from "../../../../constants/inquiry_button_type";
import {useHomeMatchPreferencesStorage} from "../../../application/hooks/use_home_match_preferences_storage";
import {LeadModalStep} from "../../../application/types/LeadModalStep";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {getStoreHash} from "../../../application/utils/get_store_hash";
import {validationMessages} from "../../../form/utils/validation_messages";
import {hitGTMEcommerceBeginCheckout} from "../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {getHomeMatchSearchRegions} from "../../../region/utils/get_homematch_regions";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {gtmCheckout} from "../../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../../tracking/google_tag_manager/gtm_event_form_open";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {applicationBaseCheckboxesOptions, applicationMoreCheckboxesOptions} from "../../constants/lead_modal_checkbox_options";
import {useLeadData} from "../../hooks/use_lead_data";
import {useSendMainLead} from "../../hooks/use_send_main_lead";
import {openLeadModal, setLeadModalMainLeadType, setMainLeadData} from "../../state/main_lead_slice";
import {GTMEventApplicationSourceSection} from "../../types/GTMEvenApplicationSourceSection";
import {ILeadModalPriceConversationChatLeadFormValues} from "../../types/ILeadModalPriceConversationChatLeadFormValues";
import {MainLeadDataPayload} from "../../types/MainLeadDataPayload";
import {normalizeLeadQuestions} from "../../utils/normalize_lead_questions";
import {FormSubmitFooter} from "../FormSubmitFooter";
import {UserContactDataForm} from "../UserContactDataForm";

interface IDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    sidebarQuestions: string[];
    sidebarUserMessage?: string;
}

interface IProps {
    vendorId: number;
    offerId: IOfferDetail["id"];
    propertyId?: IPropertyDetail["id"];
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
}

export const LeadSidebarForm = (props: IProps) => {
    const {offerId, propertyId = null, vendorId, source, sourceSection} = props;
    const dispatch = useAppDispatch();
    const {data: userData} = useGetUserProfileQuery();
    const {data: propertyData} = useGetPropertyDetailQuery(propertyId || 0, {skip: !propertyId});
    const [showMoreOptions, setShowMoreOptions] = useState(false);
    const [leadSending, setLeadSending] = useState(false);
    const storeHash = getStoreHash({vendorId, offerId, propertyId});
    const leadModalState = useAppSelector((state) => state.mainLead);
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadSource = leadModalState.source;
    const mainLeadHaveNecessaryData = Boolean(mainLeadSource);
    const wasInteractionHitRef = useRef(false);
    const {putPreferences} = useHomeMatchPreferencesStorage();
    const leadData = useLeadData({
        storeHash,
        vendorId,
        offerId,
        propertyId: propertyId || undefined
    });

    const defaultValues: IDefaultValues = {
        name: userData?.name || "",
        email: userData?.email || "",
        phone: userData?.phone ? getPhoneNumberAsObject(userData.phone) : {code: 48, number: ""},
        sidebarQuestions: []
    };

    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(formSchema)
    });

    const {handleSubmit, watch, setError, reset: resetForm} = formMethods;
    const otherOptionSelected = watch("sidebarQuestions").some((q) => q === "inny");
    const combinedOptions = showMoreOptions ? [...applicationBaseCheckboxesOptions, ...applicationMoreCheckboxesOptions] : applicationBaseCheckboxesOptions;

    const mainLeadStatePayload: MainLeadDataPayload = {
        vendorId,
        offerId,
        propertyId,
        source,
        sourceSection,
        gtmMeta: {
            openButtonSourceText: InquiryButtonTypeName.SUBMIT_APPLICATION,
            openButtonSource: GTMEventApplicationSourceSection.SIDEBAR_LEAD
        }
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (leadData?.vendor?.detail?.name || leadData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (leadData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && leadModalState.mainLeadType && leadModalState.sourceSection && leadData) {
            wasInteractionHitRef.current = true;
            console.log("checkout");
            gtmCheckout(leadData, viewType, leadModalState.mainLeadType);
            hitGTMEcommerceBeginCheckout(leadData, viewType, leadModalState.mainLeadType, leadModalState.sourceSection, formFillAction);
        }
    };

    useEffect(() => {
        if (!leadModalState.mainLeadType) {
            dispatch(setLeadModalMainLeadType("sidebar_form"));
        }
    }, [leadModalState.mainLeadType]);

    useEffect(() => {
        if (!mainLeadHaveNecessaryData) {
            //When the modal is closed, the data is reset and must be filled in again so that the lead is sent with the correct data.
            dispatch(setMainLeadData(mainLeadStatePayload));
        }
    }, [mainLeadHaveNecessaryData]);

    useEffect(() => {
        if (mainLeadHaveNecessaryData && leadModalState.mainLeadType && leadModalState.sourceSection && leadData) {
            const {name, email, phone} = defaultValues || {};
            if (name || email || phone?.number) {
                // form will be filled with data automatically
                onInteraction("autofill");
            }
        }
    }, [leadModalState.sourceSection, leadData]);

    const sendLead = useSendMainLead();

    const onSubmit = async (values: IDefaultValues) => {
        const valuesToSend: ILeadModalPriceConversationChatLeadFormValues = {
            name: values.name,
            email: values.email,
            phone: {
                code: values.phone.code,
                number: values.phone.number
            },
            marketing_email: userData?.email_marketing_consent || false,
            marketing_sms: userData?.phone_marketing_consent || false,
            additionalQuestions: normalizeLeadQuestions(combinedOptions, values.sidebarQuestions, values.sidebarUserMessage)
        };

        try {
            setLeadSending(true);
            await sendLead(valuesToSend);
            const region = propertyData?.offer?.region;

            const preferencesToSave = {
                rooms: propertyData?.rooms ? {lower: propertyData.rooms, upper: propertyData.rooms} : undefined,
                budget: propertyData?.price,
                ...(region && {regions: getHomeMatchSearchRegions([region])})
            };
            putPreferences(preferencesToSave);
            resetForm(defaultValues);
            dispatch(
                openLeadModal({
                    ...mainLeadStatePayload,
                    initialStep: LeadModalStep.MULTILEAD,
                    preventClosingWhenNoData: true
                })
            );
        } catch (error) {
            if (isValidationError(error)) {
                for (const fieldName of Object.keys(error.appError.fieldErrors)) {
                    const errorMessage = error.appError.fieldErrors[fieldName];
                    setError(fieldName as keyof IDefaultValues, {
                        message: errorMessage.join(", ")
                    });
                }
            }
        } finally {
            setLeadSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)} onClick={() => onInteraction()}>
                    <UserContactDataForm />

                    <div className={css({mb: 2})}>
                        <Text variant="body_copy_2" className={css({mb: 2})}>
                            Chcę zapytać o:
                        </Text>

                        <div>
                            <CheckBoxGroupControlled name="sidebarQuestions" options={combinedOptions} checkedMarkColor="#02D054" />

                            {showMoreOptions && otherOptionSelected && (
                                <TextareaField name="sidebarUserMessage" placeholder="Wpisz własną wiadomość do dewelopera." className={css({mb: 0.5})} />
                            )}

                            <Text variant="info_txt_1" strong onClick={() => setShowMoreOptions(!showMoreOptions)} className={moreOptionsLabel}>
                                {showMoreOptions ? "Mniej tematów" : "Więcej tematów"}
                            </Text>
                        </div>
                    </div>

                    <div className={formFooter}>
                        <FormSubmitFooter buttonText={InquiryButtonTypeName.SUBMIT_APPLICATION} isSending={leadSending || !mainLeadSource} />
                    </div>
                </form>
            </FormProvider>
        </Suspense>
    );
};

const isValidationError = (error: unknown): error is {appError: IAppError} => {
    return typeof error === "object" && error !== null && "appError" in error && (error.appError as IAppError).status === 400;
};

const formSchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()}),
    sidebarUserMessage: string().when("sidebarQuestions", {
        is: (questions: string[]) => questions.some((q) => q === "inny"),
        then: (schema) => schema.min(1, validationMessages.required).max(500, validationMessages.maxLength),
        otherwise: (schema) => schema.notRequired()
    })
});

const moreOptionsLabel = css({
    textDecoration: "underline",
    color: "var(--colors-gray-800)",
    cursor: "pointer"
});

const formFooter = css({
    position: "sticky",
    bottom: 0,
    zIndex: 20,
    backgroundColor: "white",
    pt: 2,
    mt: 1,
    borderTop: "1px solid var(--colors-gray-200)"
});
import {Suspense, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {number, object, string} from "yup";

import {Loader} from "@pg-design/loader-module";
import {getPhoneNumberAsObject, IPhoneFieldValue} from "@pg-design/phone-input-module";
import {CheckBoxGroupControlled, TextareaField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {IAppError} from "@pg-mono/request-state";

import {InquiryButtonTypeName} from "../../../../constants/inquiry_button_type";
import {useHomeMatchPreferencesStorage} from "../../../application/hooks/use_home_match_preferences_storage";
import {LeadModalStep} from "../../../application/types/LeadModalStep";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {getStoreHash} from "../../../application/utils/get_store_hash";
import {validationMessages} from "../../../form/utils/validation_messages";
import {hitGTMEcommerceBeginCheckout} from "../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_begin_checkout";
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {getHomeMatchSearchRegions} from "../../../region/utils/get_homematch_regions";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {gtmCheckout} from "../../../tracking/google_tag_manager/gtm_checkout";
import {gtmEventFormInquiry} from "../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {IGTMEventLeadForm} from "../../../tracking/google_tag_manager/gtm_event_form_open";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {applicationBaseCheckboxesOptions, applicationMoreCheckboxesOptions} from "../../constants/lead_modal_checkbox_options";
import {useLeadData} from "../../hooks/use_lead_data";
import {useSendMainLead} from "../../hooks/use_send_main_lead";
import {openLeadModal, setLeadModalMainLeadType, setMainLeadData} from "../../state/main_lead_slice";
import {GTMEventApplicationSourceSection} from "../../types/GTMEvenApplicationSourceSection";
import {ILeadModalPriceConversationChatLeadFormValues} from "../../types/ILeadModalPriceConversationChatLeadFormValues";
import {MainLeadDataPayload} from "../../types/MainLeadDataPayload";
import {normalizeLeadQuestions} from "../../utils/normalize_lead_questions";
import {FormSubmitFooter} from "../FormSubmitFooter";
import {UserContactDataForm} from "../UserContactDataForm";

interface IDefaultValues {
    name: string;
    email: string;
    phone: IPhoneFieldValue;
    sidebarQuestions: string[];
    sidebarUserMessage?: string;
}

interface IProps {
    vendorId: number;
    offerId: IOfferDetail["id"];
    propertyId?: IPropertyDetail["id"];
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
}

export const LeadSidebarForm = (props: IProps) => {
    const {offerId, propertyId = null, vendorId, source, sourceSection} = props;
    const dispatch = useAppDispatch();
    const {data: userData} = useGetUserProfileQuery();
    const {data: propertyData} = useGetPropertyDetailQuery(propertyId || 0, {skip: !propertyId});
    const [showMoreOptions, setShowMoreOptions] = useState(false);
    const [leadSending, setLeadSending] = useState(false);
    const storeHash = getStoreHash({vendorId, offerId, propertyId});
    const leadModalState = useAppSelector((state) => state.mainLead);
    const viewType = useAppSelector((state) => state.viewType.current);
    const mainLeadSource = leadModalState.source;
    const mainLeadHaveNecessaryData = Boolean(mainLeadSource);
    const wasInteractionHitRef = useRef(false);
    const {putPreferences} = useHomeMatchPreferencesStorage();
    const leadData = useLeadData({
        storeHash,
        vendorId,
        offerId,
        propertyId: propertyId || undefined
    });

    const defaultValues: IDefaultValues = {
        name: userData?.name || "",
        email: userData?.email || "",
        phone: userData?.phone ? getPhoneNumberAsObject(userData.phone) : {code: 48, number: ""},
        sidebarQuestions: []
    };

    const formMethods = useForm({
        defaultValues,
        resolver: yupResolver(formSchema)
    });

    const {handleSubmit, watch, setError, reset: resetForm} = formMethods;
    const otherOptionSelected = watch("sidebarQuestions").some((q) => q === "inny");
    const combinedOptions = showMoreOptions ? [...applicationBaseCheckboxesOptions, ...applicationMoreCheckboxesOptions] : applicationBaseCheckboxesOptions;

    const mainLeadStatePayload: MainLeadDataPayload = {
        vendorId,
        offerId,
        propertyId,
        source,
        sourceSection,
        gtmMeta: {
            openButtonSourceText: InquiryButtonTypeName.SUBMIT_APPLICATION,
            openButtonSource: GTMEventApplicationSourceSection.SIDEBAR_LEAD
        }
    };

    const gtmData: IGTMEventLeadForm = {
        clickText: leadModalState.gtmMeta.openButtonSourceText ?? "",
        source: leadModalState.gtmMeta.openButtonSource ?? "",
        vendorName: (leadData?.vendor?.detail?.name || leadData?.vendor?.detail?.id.toString()) ?? "",
        offerName: (leadData?.offer?.detail?.name || offerId?.toString()) ?? ""
    };

    const onInteraction = (formFillAction: "autofill" | "manual_fill" = "manual_fill") => {
        if (!wasInteractionHitRef.current && leadModalState.mainLeadType && leadModalState.sourceSection && leadData) {
            wasInteractionHitRef.current = true;
            console.log("checkout");
            gtmCheckout(leadData, viewType, leadModalState.mainLeadType);
            hitGTMEcommerceBeginCheckout(leadData, viewType, leadModalState.mainLeadType, leadModalState.sourceSection, formFillAction);
        }
    };

    useEffect(() => {
        if (!leadModalState.mainLeadType) {
            dispatch(setLeadModalMainLeadType("sidebar_form"));
        }
    }, [leadModalState.mainLeadType]);

    useEffect(() => {
        if (!mainLeadHaveNecessaryData) {
            //When the modal is closed, the data is reset and must be filled in again so that the lead is sent with the correct data.
            dispatch(setMainLeadData(mainLeadStatePayload));
        }
    }, [mainLeadHaveNecessaryData]);

    useEffect(() => {
        if (mainLeadHaveNecessaryData && leadModalState.mainLeadType && leadModalState.sourceSection && leadData) {
            const {name, email, phone} = defaultValues || {};
            if (name || email || phone?.number) {
                // form will be filled with data automatically
                onInteraction("autofill");
            }
        }
    }, [leadModalState.sourceSection, leadData]);

    const sendLead = useSendMainLead();

    const onSubmit = async (values: IDefaultValues) => {
        const valuesToSend: ILeadModalPriceConversationChatLeadFormValues = {
            name: values.name,
            email: values.email,
            phone: {
                code: values.phone.code,
                number: values.phone.number
            },
            marketing_email: userData?.email_marketing_consent || false,
            marketing_sms: userData?.phone_marketing_consent || false,
            additionalQuestions: normalizeLeadQuestions(combinedOptions, values.sidebarQuestions, values.sidebarUserMessage)
        };

        try {
            setLeadSending(true);
            await sendLead(valuesToSend);
            const region = propertyData?.offer?.region;

            const preferencesToSave = {
                rooms: propertyData?.rooms ? {lower: propertyData.rooms, upper: propertyData.rooms} : undefined,
                budget: propertyData?.price,
                ...(region && {regions: getHomeMatchSearchRegions([region])})
            };
            putPreferences(preferencesToSave);
            resetForm(defaultValues);
            dispatch(
                openLeadModal({
                    ...mainLeadStatePayload,
                    initialStep: LeadModalStep.MULTILEAD,
                    preventClosingWhenNoData: true
                })
            );
        } catch (error) {
            if (isValidationError(error)) {
                for (const fieldName of Object.keys(error.appError.fieldErrors)) {
                    const errorMessage = error.appError.fieldErrors[fieldName];
                    setError(fieldName as keyof IDefaultValues, {
                        message: errorMessage.join(", ")
                    });
                }
            }
        } finally {
            setLeadSending(false);
            gtmEventFormInquiry(gtmData.clickText, gtmData.source, gtmData.vendorName, gtmData.offerName);
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)} onClick={() => onInteraction()}>
                    <UserContactDataForm />

                    <div className={css({mb: 2})}>
                        <Text variant="body_copy_2" className={css({mb: 2})}>
                            Chcę zapytać o:
                        </Text>

                        <div>
                            <CheckBoxGroupControlled name="sidebarQuestions" options={combinedOptions} checkedMarkColor="#02D054" />

                            {showMoreOptions && otherOptionSelected && (
                                <TextareaField name="sidebarUserMessage" placeholder="Wpisz własną wiadomość do dewelopera." className={css({mb: 0.5})} />
                            )}

                            <Text variant="info_txt_1" strong onClick={() => setShowMoreOptions(!showMoreOptions)} className={moreOptionsLabel}>
                                {showMoreOptions ? "Mniej tematów" : "Więcej tematów"}
                            </Text>
                        </div>
                    </div>

                    <div className={formFooter}>
                        <FormSubmitFooter buttonText={InquiryButtonTypeName.SUBMIT_APPLICATION} isSending={leadSending || !mainLeadSource} />
                    </div>
                </form>
            </FormProvider>
        </Suspense>
    );
};

const isValidationError = (error: unknown): error is {appError: IAppError} => {
    return typeof error === "object" && error !== null && "appError" in error && (error.appError as IAppError).status === 400;
};

const formSchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()}),
    sidebarUserMessage: string().when("sidebarQuestions", {
        is: (questions: string[]) => questions.some((q) => q === "inny"),
        then: (schema) => schema.min(1, validationMessages.required).max(500, validationMessages.maxLength),
        otherwise: (schema) => schema.notRequired()
    })
});

const moreOptionsLabel = css({
    textDecoration: "underline",
    color: "var(--colors-gray-800)",
    cursor: "pointer"
});

const formFooter = css({
    position: "sticky",
    bottom: 0,
    zIndex: 20,
    backgroundColor: "white",
    pt: 2,
    mt: 1,
    borderTop: "1px solid var(--colors-gray-200)"
});
